// React
import React, { useEffect, useMemo, useState } from 'react';
// Mantine
import {
  Button,
  Divider,
  Flex,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useNotification } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';

// components
import {
  ChainCardHeader,
  ChainSidebar,
  ChainTimeline,
} from '../ClaimActionsManagement';

// Api
import {
  useCreateActionResponse,
  useCreateActionRequest,
  useUpdateActionRequest,
  useUpdateActionResponse,
} from 'api/resource/actionManagement';
import { useFetchResponseAttachments } from 'hooks/useFetchResponseAttachments';
import { uploadFileToS3 } from 'utils/fileUpload';

// Interfaces
import { ActionRequest, ActionResponse, Claim } from 'interfaces';
import { ActionRequestStatus, ActionRequestType } from '@billing/validation';

// Utils
import { compareDateAsc, nowISO, toJSDate } from 'utils/dateUtils';
import { v4 } from 'uuid';

// Store
import { useCurrentUserStore } from 'store';
import { useIsProvider } from 'hooks/useIsProvider';

import * as Sentry from '@sentry/react';
import { FileWithPath } from '@mantine/dropzone';

interface ViewClaimActionsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  claim: Claim | null;
}

const statusColor: Record<ActionRequestStatus, string> = {
  [ActionRequestStatus.REQUESTED]: 'blue',
  [ActionRequestStatus.SUBMITTED]: 'indigo',
  [ActionRequestStatus.SATISFIED]: 'green',
  [ActionRequestStatus.REJECTED]: 'red',
  [ActionRequestStatus.CANCELLED]: 'gray',
};

const typeColor: Record<ActionRequestType, string> = {
  [ActionRequestType.INFORMATION]: 'yellow',
  [ActionRequestType.SUPPORTING_DOCUMENTS]: 'orange',
};

const providerVisibleActionRequestStatuses = new Set<ActionRequestStatus>([
  ActionRequestStatus.REQUESTED,
  ActionRequestStatus.SUBMITTED,
  ActionRequestStatus.REJECTED,
]);

export const formatTypeLabel = (type: ActionRequestType) =>
  type
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

export type ChainStep = { request: ActionRequest; response?: ActionResponse };

export type Chain = {
  root: ActionRequest;
  steps: ChainStep[];
  latestRequest: ActionRequest;
};

/**
 * Groups action requests and responses into independent chains.
 * Each chain starts from a root request (no parentRequestID) and follows the chain of requests
 * via parentRequestID, pairing each request with its earliest response (if any).
 *
 * @param requests - All ActionRequest objects for the claim
 * @param responses - All ActionResponse objects for the claim
 * @returns Array of Chain objects, each representing a request/response chain
 */
const buildChains = (
  requests: ActionRequest[],
  responses: ActionResponse[]
): Chain[] => {
  // 1. Find root requests sorted by creation time
  const roots = requests
    .filter((r) => !r.parentRequestID)
    .sort((a, b) => {
      return String(a.createdAt).localeCompare(String(b.createdAt));
    });

  // 2. For each root, build the chain of requests/responses
  return roots.map((root) => {
    const steps: ChainStep[] = [];
    let current: ActionRequest | undefined = root;
    let latestRequest = root;

    // 3. Traverse the chain: for each request, find its earliest response and next child request
    while (current) {
      // Find the earliest response for this request (if any)
      const response = responses
        .filter((x) => x.actionRequestID === current.id)
        .sort((a, b) => compareDateAsc(a.createdAt, b.createdAt))[0];
      // Add the step (request + response) to the chain
      steps.push({ request: current, response });
      latestRequest = current;
      // Move to the next request in the chain (child whose parentRequestID is current.id)
      current = requests.find((r) => r.parentRequestID === current?.id);
    }

    return { root, steps, latestRequest };
  });
};

export const ViewClaimActionsModal: React.FC<ViewClaimActionsModalProps> = ({
  open,
  setOpen,
  claim,
}) => {
  const mode = useComputedColorScheme();
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const isProvider = useIsProvider();
  const { open: openNotification } = useNotification();

  const createActionResponse = useCreateActionResponse();
  const createActionRequest = useCreateActionRequest();
  const updateActionRequest = useUpdateActionRequest();
  const updateActionResponse = useUpdateActionResponse();

  const { id: userID, providerID, providers } = useCurrentUserStore();

  const actionRequests: ActionRequest[] = claim?.actionRequests || [];
  const actionResponses: ActionResponse[] = claim?.actionResponses || [];
  const claimID = claim?.id;

  const providerScopeIDs = useMemo(
    () =>
      Array.from(
        new Set(
          [providerID, ...(providers ?? []).map((provider) => provider.id)]
            .map((id) => id?.trim())
            .filter((id): id is string => Boolean(id))
        )
      ),
    [providerID, providers]
  );

  const visibleActionRequests = useMemo(() => {
    if (!isProvider) return actionRequests;
    if (providerScopeIDs.length === 0) return [];

    return actionRequests.filter(
      (request) =>
        providerScopeIDs.includes(request.providerID) &&
        providerVisibleActionRequestStatuses.has(request.status)
    );
  }, [actionRequests, isProvider, providerScopeIDs]);

  const visibleActionResponses = useMemo(() => {
    if (!isProvider) return actionResponses;

    const visibleRequestIDs = new Set(
      visibleActionRequests.map((request) => request.id)
    );

    return actionResponses.filter((response) =>
      visibleRequestIDs.has(response.actionRequestID)
    );
  }, [actionResponses, isProvider, visibleActionRequests]);

  const { attachmentsByResponseID, loading: attachmentsLoading } =
    useFetchResponseAttachments(open, claimID, visibleActionResponses);

  const chains = useMemo(
    () => buildChains(visibleActionRequests, visibleActionResponses),
    [visibleActionRequests, visibleActionResponses]
  );

  const showSidebar = chains.length > 1;

  const [selectedRootID, setSelectedRootID] = useState<string | null>(
    chains[0]?.root?.id ?? null
  );

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectContext, setRejectContext] = useState<{
    request: ActionRequest;
    response: ActionResponse;
  } | null>(null);

  useEffect(() => {
    if (!chains.length) {
      setSelectedRootID(null);
      return;
    }
    if (!selectedRootID || !chains.find((c) => c.root.id === selectedRootID)) {
      setSelectedRootID(chains[0].root.id);
    }
  }, [open, chains.length, selectedRootID]);

  const selectedChain =
    chains.find((c) => c.root.id === selectedRootID) ?? chains[0];

  const openRejectDialog = (
    request: ActionRequest,
    response: ActionResponse
  ) => {
    setRejectContext({ request, response });
    setRejectMessage('');
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectMessage('');
    setRejectContext(null);
  };

  const updateResponse = async (response: ActionResponse) => {
    try {
      await updateActionResponse.mutateAsync({
        ...response,
        reviewedBy: userID,
        reviewedAt: toJSDate(nowISO()),
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleRejectResponse = async () => {
    if (!rejectContext) return;

    try {
      setRejectLoading(true);

      const { request, response } = rejectContext;

      const updateRequest: ActionRequest = {
        ...request,
        createdAt: toJSDate(request.createdAt.toString()) ?? toJSDate(nowISO()),
        status: ActionRequestStatus.REJECTED,
      };

      const newRequest: Partial<ActionRequest> = {
        id: v4(),
        status: ActionRequestStatus.REQUESTED,
        providerID: request.providerID,
        createdAt: toJSDate(nowISO()),
        claimID: request.claimID,
        parentRequestID: request.id,
        requestedBy: userID,
        type: request.type,
        message: rejectMessage,
      };

      await updateResponse(response);
      await updateActionRequest.mutateAsync(updateRequest);
      await createActionRequest.mutateAsync(newRequest);

      queryClient.invalidateQueries();
      setOpen(false);
      closeRejectDialog();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setRejectLoading(false);
    }
  };

  const handleSatisfyResponse = async (
    request: ActionRequest,
    response: ActionResponse
  ) => {
    try {
      const actionRequestID = response.actionRequestID;

      if (!actionRequestID || actionRequestID !== request.id) {
        Sentry.captureMessage('Mismatched ActionRequest ID in response', {
          level: 'error',
        });
        return;
      }

      const updateAction: ActionRequest = {
        ...request,
        createdAt: toJSDate(request.createdAt.toString()) ?? toJSDate(nowISO()),
        status: ActionRequestStatus.SATISFIED,
      };

      await updateActionRequest.mutateAsync(updateAction);
      await updateResponse(response);
      setOpen(false);
      queryClient.invalidateQueries();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleCancelResponse = async (
    request: ActionRequest,
    response: ActionResponse
  ) => {
    try {
      const updateRequest: ActionRequest = {
        ...request,
        status: ActionRequestStatus.CANCELLED,
      };

      await updateActionRequest.mutateAsync(updateRequest);
      await updateResponse(response);
      setOpen(false);
      queryClient.invalidateQueries();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleProviderSubmitResponse = async ({
    request,
    message,
    files,
  }: {
    request: ActionRequest;
    message: string;
    files: FileWithPath[];
  }) => {
    if (!claimID) return;
    if (
      isProvider &&
      (providerScopeIDs.length === 0 ||
        !providerScopeIDs.includes(request.providerID))
    ) {
      throw new Error('Provider cannot respond to this action request.');
    }

    const responseID = v4();
    const attachmentBucket = `claim-attachments-${
      import.meta.env.VITE_ACCOUNT_ID
    }`;
    const attachmentKeys = files.map(
      (file) =>
        `claims/${claimID}/actionResponses/${responseID}/${file.name.trim()}`
    );

    try {
      if (files.length) {
        const uploadResults = await uploadFileToS3(
          files,
          attachmentBucket,
          attachmentKeys
        );
        const failedUploadCount = uploadResults.filter(
          (result) => !result
        ).length;

        if (failedUploadCount > 0) {
          throw new Error('One or more response attachments failed to upload.');
        }
      }

      const response: ActionResponse = {
        id: responseID,
        actionRequestID: request.id,
        providerID: request.providerID,
        claimID,
        createdAt: toJSDate(nowISO()) ?? new Date(),
        reviewedBy: null,
        reviewedAt: null,
        message: message.trim(),
      };

      const updatedRequest: ActionRequest = {
        ...request,
        createdAt: toJSDate(request.createdAt.toString()) ?? toJSDate(nowISO()),
        status: ActionRequestStatus.SUBMITTED,
      };

      await createActionResponse.mutateAsync(response);
      await updateActionRequest.mutateAsync(updatedRequest);
      await queryClient.invalidateQueries();
      setOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      openNotification?.({
        type: 'error',
        message: 'Failed to submit action response.',
      });
      throw error;
    }
  };

  return (
    <>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        closeOnEscape
        withCloseButton
        size="xl"
        padding={'xl'}
        centered
        radius="lg"
        data-testid="view-claim-actions-modal"
        overlayProps={{ blur: 4, opacity: 0.55 }}
        styles={{
          content: {
            minWidth: '60%',
            background:
              mode === 'dark'
                ? theme.colors.dark[7]
                : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${
              mode === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]
            }`,
          },
        }}
        title={
          <Stack gap={2}>
            <Title order={3} data-testid="view-claim-actions-title">
              {'View Claim Actions'}
            </Title>
            <Text
              size="xs"
              c="dimmed"
              data-testid="view-claim-actions-subtitle"
            >
              {'Review request/response chains for this claim.'}
            </Text>
          </Stack>
        }
      >
        <Stack gap="md" m="md">
          {chains.length === 0 ? (
            <Text
              size="sm"
              c="dimmed"
              ta="center"
              data-testid="view-claim-actions-empty"
            >
              {'No action requests have been created for this claim yet.'}
            </Text>
          ) : (
            <Flex gap="md" align="stretch" style={{ minHeight: 520 }}>
              {showSidebar && (
                <ChainSidebar
                  chains={chains}
                  selectedRootID={selectedRootID}
                  onSelect={setSelectedRootID}
                  statusColor={statusColor}
                  typeColor={typeColor}
                />
              )}

              <Paper
                radius="lg"
                withBorder
                p="md"
                style={{
                  flex: 1,
                  background:
                    mode === 'dark' ? theme.colors.dark[8] : theme.white,
                }}
              >
                <Stack gap="md">
                  <ChainCardHeader
                    chain={selectedChain}
                    statusColor={statusColor}
                    typeColor={typeColor}
                    typeLabel={formatTypeLabel(selectedChain.root.type)}
                  />
                  <Divider />
                  <ChainTimeline
                    chain={selectedChain}
                    attachmentsByResponseID={attachmentsByResponseID}
                    attachmentLoading={attachmentsLoading}
                    isProviderMode={isProvider}
                    onSubmitProviderResponse={handleProviderSubmitResponse}
                    onSatisfyResponse={handleSatisfyResponse}
                    onCancelResponse={handleCancelResponse}
                    openRejectDialog={openRejectDialog}
                    typeLabel={formatTypeLabel(selectedChain.root.type)}
                    statusColor={statusColor}
                    typeColor={typeColor}
                  />
                </Stack>
              </Paper>
            </Flex>
          )}
        </Stack>
      </Modal>
      <Modal
        opened={rejectDialogOpen}
        onClose={closeRejectDialog}
        closeOnEscape
        withCloseButton
        size="lg"
        centered
        radius="lg"
        overlayProps={{ blur: 4, opacity: 0.55 }}
        styles={{
          header: { paddingBottom: theme.spacing.xs },
          body: { paddingTop: theme.spacing.xs },
          content: {
            background:
              mode === 'dark'
                ? theme.colors.dark[7]
                : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${
              mode === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]
            }`,
          },
        }}
        title={
          <Stack gap={2}>
            <Title order={4}>{'Reject Response'}</Title>
            <Text size="xs" c="dimmed">
              {'Add a message explaining what needs to be fixed.'}
            </Text>
          </Stack>
        }
      >
        {rejectLoading ? (
          <Flex justify="center" align="center">
            <Loader />
          </Flex>
        ) : (
          <Stack gap="md">
            <Textarea
              label="Message"
              placeholder="Tell the provider what was wrong and why it was rejected"
              value={rejectMessage}
              onChange={(event) => setRejectMessage(event.currentTarget.value)}
              data-testid="view-claim-actions-reject-message"
              autosize
              minRows={4}
              radius="md"
            />
            <Divider />
            <Flex gap="sm" align="center" justify="center" mt="md">
              <Button
                variant="outline"
                color="grey"
                c={mode === 'dark' ? 'white' : 'black'}
                size="md"
                onClick={closeRejectDialog}
              >
                {'Cancel'}
              </Button>
              <Button
                color="green"
                c={mode === 'dark' ? 'white' : 'black'}
                size="md"
                disabled={!rejectMessage.trim()}
                data-testid="view-claim-actions-reject-submit"
                onClick={handleRejectResponse}
              >
                {'Send New Request'}
              </Button>
            </Flex>
          </Stack>
        )}
      </Modal>
    </>
  );
};
